import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useCreateAvailabilityRequest,
  useAvailabilityRequests,
  useAvailabilityGrid,
  useUpdateAvailabilityRequestStatus,
  useSendAvailabilityReminder,
  type RequestStatus,
} from "@/api/scheduling";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Send,
  Check,
  Loader2,
  Users,
  CheckCircle2,
  Circle,
  MoreVertical,
  Lock,
  Unlock,
  XCircle,
  Bell,
} from "lucide-react";

interface RequestAvailabilityCardProps {
  weekStart: Date;
  dispatchCenterId: string;
}

export function RequestAvailabilityCard({
  weekStart,
  dispatchCenterId,
}: RequestAvailabilityCardProps) {
  const [message, setMessage] = useState("");
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);

  const weekStartStr = format(weekStart, "yyyy-MM-dd");

  const { data: existingRequests, isLoading } = useAvailabilityRequests({
    dispatchCenterId,
    weekStart: weekStartStr,
  });

  const { data: operators } = useAvailabilityGrid({
    weekStart: weekStartStr,
    dispatchCenterId,
  });

  const createRequest = useCreateAvailabilityRequest({
    onSuccess: () => {
      toast.success("Availability request sent to operators");
      setMessage("");
      setSelectedOperatorIds([]);
    },
    onError: (error) => {
      toast.error("Failed to send request", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  const updateStatus = useUpdateAvailabilityRequestStatus({
    onSuccess: (data) => {
      const statusMessages: Record<RequestStatus, string> = {
        OPEN: "Request reopened - operators can now submit/edit",
        CLOSED: "Request closed - no more submissions allowed",
        CANCELLED: "Request cancelled",
      };
      toast.success(statusMessages[data.status]);
    },
    onError: (error) => {
      toast.error("Failed to update status", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  const sendReminder = useSendAvailabilityReminder({
    onSuccess: (data) => {
      if (data.remindedCount === 0) {
        toast.info("All operators have already submitted");
      } else {
        toast.success(
          `Reminder sent to ${data.remindedCount} operator${data.remindedCount === 1 ? "" : "s"}`
        );
      }
    },
    onError: (error) => {
      toast.error("Failed to send reminder", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  const existingRequest = existingRequests?.[0];

  const toggleOperator = (operatorId: string) => {
    setSelectedOperatorIds((prev) =>
      prev.includes(operatorId)
        ? prev.filter((id) => id !== operatorId)
        : [...prev, operatorId]
    );
  };

  const selectAll = () => {
    if (operators) {
      setSelectedOperatorIds(operators.map((op) => op.operatorId));
    }
  };

  const selectNone = () => {
    setSelectedOperatorIds([]);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  // If request exists, show status
  if (existingRequest) {
    const respondedOperators =
      existingRequest.operators?.filter((op) => op.submitted) ?? [];
    const pendingOperators =
      existingRequest.operators?.filter((op) => !op.submitted) ?? [];
    const status = existingRequest.status ?? "OPEN";

    const statusConfig: Record<
      RequestStatus,
      { label: string; variant: "default" | "secondary" | "destructive" }
    > = {
      OPEN: { label: "Open", variant: "default" },
      CLOSED: { label: "Closed", variant: "secondary" },
      CANCELLED: { label: "Cancelled", variant: "destructive" },
    };

    const handleStatusChange = (newStatus: RequestStatus) => {
      updateStatus.mutate({ requestId: existingRequest.id, status: newStatus });
    };

    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              <CardTitle className="text-lg">Request Sent</CardTitle>
              <Badge variant={statusConfig[status].variant}>
                {statusConfig[status].label}
              </Badge>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {status === "OPEN" && (
                  <>
                    {pendingOperators.length > 0 && (
                      <DropdownMenuItem
                        onClick={() => sendReminder.mutate(existingRequest.id)}
                        disabled={sendReminder.isPending}
                      >
                        <Bell className="mr-2 h-4 w-4" />
                        Send Reminder ({pendingOperators.length})
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleStatusChange("CLOSED")}
                      disabled={updateStatus.isPending}
                    >
                      <Lock className="mr-2 h-4 w-4" />
                      Close Request
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleStatusChange("CANCELLED")}
                      disabled={updateStatus.isPending}
                      className="text-destructive"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancel Request
                    </DropdownMenuItem>
                  </>
                )}
                {status === "CLOSED" && (
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("OPEN")}
                    disabled={updateStatus.isPending}
                  >
                    <Unlock className="mr-2 h-4 w-4" />
                    Reopen Request
                  </DropdownMenuItem>
                )}
                {status === "CANCELLED" && (
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("OPEN")}
                    disabled={updateStatus.isPending}
                  >
                    <Unlock className="mr-2 h-4 w-4" />
                    Reopen Request
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <CardDescription>
            Sent on{" "}
            {format(new Date(existingRequest.createdAt), "MMM d 'at' h:mm a")}
            {existingRequest.closedAt && (
              <span className="ml-2">
                | {status === "CLOSED" ? "Closed" : "Cancelled"} on{" "}
                {format(
                  new Date(existingRequest.closedAt),
                  "MMM d 'at' h:mm a"
                )}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {existingRequest.message && (
            <div className="p-2 bg-gray-50 rounded text-sm italic">
              "{existingRequest.message}"
            </div>
          )}

          <div className="text-sm">
            <span className="font-medium text-green-600">
              {existingRequest.submittedCount}
            </span>
            <span className="text-gray-500">
              {" "}
              / {existingRequest.totalOperators} submitted
            </span>
          </div>

          <ScrollArea className="h-40">
            <div className="space-y-1">
              {respondedOperators.map((op) => (
                <div
                  key={op.id}
                  className="flex items-center gap-2 text-sm py-1"
                >
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>{op.name}</span>
                </div>
              ))}
              {pendingOperators.map((op) => (
                <div
                  key={op.id}
                  className="flex items-center gap-2 text-sm py-1 text-gray-400"
                >
                  <Circle className="h-4 w-4" />
                  <span>{op.name}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  // No request yet - show form
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Request Availability</CardTitle>
        <CardDescription>
          Week of {format(weekStart, "MMM d, yyyy")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Operator Selection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Operators ({selectedOperatorIds.length}/{operators?.length ?? 0})
            </Label>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAll}
                className="h-7 text-xs"
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={selectNone}
                className="h-7 text-xs"
              >
                None
              </Button>
            </div>
          </div>
          <ScrollArea className="h-32 border rounded-md p-2">
            <div className="space-y-2">
              {operators?.map((op) => (
                <div key={op.operatorId} className="flex items-center gap-2">
                  <Checkbox
                    id={op.operatorId}
                    checked={selectedOperatorIds.includes(op.operatorId)}
                    onCheckedChange={() => toggleOperator(op.operatorId)}
                  />
                  <Label
                    htmlFor={op.operatorId}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {op.name}
                    {op.availability && (
                      <span className="text-green-600 text-xs ml-2">
                        (has availability)
                      </span>
                    )}
                  </Label>
                </div>
              ))}
              {(!operators || operators.length === 0) && (
                <div className="text-sm text-gray-400 text-center py-2">
                  No operators in this dispatch center
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label>Message (optional)</Label>
          <Textarea
            placeholder="Any notes for operators..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
          />
        </div>

        {/* Send Button */}
        <Button
          onClick={() =>
            createRequest.mutate({
              dispatchCenterId,
              weekStart: weekStartStr,
              message: message || undefined,
              operatorIds:
                selectedOperatorIds.length > 0
                  ? selectedOperatorIds
                  : undefined,
            })
          }
          disabled={createRequest.isPending || selectedOperatorIds.length === 0}
          className="w-full"
        >
          {createRequest.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send to {selectedOperatorIds.length} Operator
              {selectedOperatorIds.length !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
